/**
 * Outil « Fonctions de référence » (Boîte à outils, 2nde → 1re).
 *
 * x², 1/x, √x, x³, |x| : un point M court sur la courbe (au doigt, aux
 * flèches ou au compteur), son image s'écrit EXACTEMENT, et chaque morceau
 * du tableau de variations se remplit quand M l'a parcouru. « Symétrie »
 * montre M′ d'abscisse −x : f(−x) = f(x) (paire) ou −f(x) (impaire) ; √x
 * n'est définie que pour x ≥ 0. Définitions : `reference.ts`.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, SECONDARY_BUTTON, Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { makeFrame, type Frame } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { DragHandle, FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { exactImage, fnById, FUNCTIONS, revealed, type FnId, type RefFn } from './reference';

const SIZE = 320;
const CURVE = 'var(--color-primary-500)';
const POINT = 'var(--color-ink-900)';
const MIRROR = 'var(--color-accent-700)';
const START: Record<FnId, number> = { square: -2, inverse: 1, sqrt: 4, cube: -1, abs: -3 };

const tx = (s: string) => s.replace(/,/g, '{,}');
const num = (v: number) => formatNumber(v, 2);

/** Chemin de la courbe, coupé là où elle sort de la fenêtre ou n'est pas définie. */
function curvePath(fn: RefFn, frame: Frame): string {
  const { vp } = frame;
  const N = 400;
  let d = '';
  let pen = false;
  for (let i = 0; i <= N; i++) {
    const x = vp.xMin + ((vp.xMax - vp.xMin) * i) / N;
    const ok = fn.defined(x) && !(fn.id === 'inverse' && Math.abs(x) < 1e-6);
    const y = ok ? fn.f(x) : NaN;
    if (!ok || !Number.isFinite(y) || y < vp.yMin - 2 || y > vp.yMax + 2) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${frame.tx(x).toFixed(1)},${frame.ty(y).toFixed(1)} `;
    pen = true;
  }
  // racine carrée : la courbe commence pile en (0 ; 0)
  if (fn.id === 'sqrt')
    d = `M${frame.tx(0).toFixed(1)},${frame.ty(0).toFixed(1)} ` + d.replace(/^M/, 'L');
  return d;
}

export function ReferenceFunctionsTool({ onSend, sendCost }: ToolProps) {
  const [id, setId] = useState<FnId>('square');
  const fn = fnById(id);
  const frame = makeFrame(fn.vp, SIZE);
  const [x, setX] = useState(START.square);
  const [seen, setSeen] = useState<number[]>([]);
  const [mirror, setMirror] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const moveTo = (raw: number) => {
    let v = Math.round(raw * 2) / 2;
    v = Math.min(fn.xMax, Math.max(fn.xMin, v));
    if (!fn.defined(v)) {
      // 1/x : on saute 0 dans le sens du mouvement
      v = v + (v >= x ? 0.5 : -0.5);
      if (!fn.defined(v) || v > fn.xMax || v < fn.xMin) return;
    }
    if (v === x) return;
    const more = revealed(fn, x, v);
    if (more.length) setSeen((s) => [...new Set([...s, ...more])]);
    setX(v);
  };

  const drag = useDrag<'M'>({
    svgRef,
    frame,
    ids: ['M'],
    pointOf: () => ({ x, y: fn.f(x) }),
    onDrag: (_, p) => moveTo(p.x),
    onBackground: (p) => moveTo(p.x),
    keyStep: 0.5,
  });

  const choose = (next: FnId) => {
    setId(next);
    setX(START[next]);
    setSeen([]);
  };

  const y = fn.f(x);
  const img = exactImage(fn, x);
  const irrational = img.plain.includes('√') || img.plain.includes('/');
  const m = { x: frame.tx(x), y: frame.ty(y) };
  const xm = -x;
  const showMirror = mirror && fn.defined(xm) && xm !== x;
  const mm = showMirror ? { x: frame.tx(xm), y: frame.ty(fn.f(xm)) } : null;
  const ox = frame.tx(0);
  const oy = frame.ty(0);
  const allSeen = seen.length === fn.pieces.length;

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Fonction"
        value={id}
        options={FUNCTIONS.map((f) => ({ value: f.id, label: f.label }))}
        onChange={choose}
      />
      <p className="text-ink-700 text-sm">
        <MathText>{`$f(x) = ${fn.tex}$`}</MathText> : {fn.name}, définie sur {fn.domain}. Fais
        courir M sur la courbe : le tableau de variations se remplit.
      </p>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Courbe de la ${fn.name}.`}
          data-testid="rf-figure"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} />
          <path d={curvePath(fn, frame)} fill="none" stroke={CURVE} strokeWidth={3} />

          {/* projections de M sur les axes */}
          <line
            x1={m.x}
            y1={m.y}
            x2={m.x}
            y2={oy}
            stroke={POINT}
            strokeDasharray="4 4"
            strokeOpacity={0.6}
          />
          <line
            x1={m.x}
            y1={m.y}
            x2={ox}
            y2={m.y}
            stroke={POINT}
            strokeDasharray="4 4"
            strokeOpacity={0.6}
          />
          {mm && (
            <g>
              <line
                x1={m.x}
                y1={m.y}
                x2={mm.x}
                y2={mm.y}
                stroke={MIRROR}
                strokeDasharray="6 4"
                strokeWidth={1.5}
              />
              <circle cx={mm.x} cy={mm.y} r={6} fill={MIRROR} />
              <HaloText x={mm.x + 8} y={mm.y - 8} fontSize={14} fontWeight={700} fill={MIRROR}>
                M′
              </HaloText>
            </g>
          )}
          <DragHandle
            x={m.x}
            y={m.y}
            color={POINT}
            label="M"
            labelDx={m.x > SIZE - 28 ? -14 : 12}
            labelDy={m.y < 24 ? 18 : -14}
            name={`Point M d’abscisse ${num(x)}, image ${img.plain}`}
            active={drag.active === 'M'}
            keyProps={drag.keyProps('M')}
          />
        </svg>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <Stepper
            label="x"
            value={x}
            min={fn.xMin}
            max={fn.xMax}
            step={0.5}
            format={(v) => num(v).replace('-', '−')}
            onChange={moveTo}
          />
        </div>
        <ToggleChip on={mirror} onClick={() => setMirror((v) => !v)}>
          Symétrie
        </ToggleChip>
      </div>

      <p className="sr-only" aria-live="polite">
        M({num(x)} ; {img.plain})
      </p>
      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
        data-testid="rf-image"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-lg">
            {`$f(${tx(num(x))}) = ${fn.id === 'inverse' ? `\\frac{1}{${tx(num(x))}} = ` : ''}${img.tex}${irrational ? ` \\approx ${tx(num(img.value))}` : ''}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={img.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: img.atoms })}
            />
          )}
        </div>
        {mirror && (
          <p className="text-ink-700 text-sm" data-testid="rf-parity">
            {fn.parity === 'even' &&
              `f(${num(-x)}) = ${exactImage(fn, -x).plain} = f(${num(x)}) : f(−x) = f(x) pour tout x, la courbe est symétrique par rapport à l’axe des ordonnées (fonction paire).`}
            {fn.parity === 'odd' &&
              `f(${num(-x)}) = ${exactImage(fn, -x).plain} = −f(${num(x)}) : f(−x) = −f(x) pour tout x${fn.id === 'inverse' ? ' ≠ 0' : ''}, la courbe est symétrique par rapport à l’origine (fonction impaire).`}
            {fn.parity === 'none' &&
              '√x n’existe que pour x ≥ 0 : si x > 0, −x n’a pas d’image. La fonction n’est ni paire ni impaire.'}
          </p>
        )}
      </section>

      <section aria-label="Tableau de variations" className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-ink-500 min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide">
            Tableau de variations ({seen.length}/{fn.pieces.length})
          </p>
          {!allSeen && (
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => setSeen(fn.pieces.map((_, i) => i))}
            >
              Tout afficher
            </button>
          )}
        </div>
        <VariationTable fn={fn} seen={seen} />
        <p className="sr-only" aria-live="polite">
          {fn.pieces
            .map((p, i) =>
              seen.includes(i)
                ? `${p.dir === 'up' ? 'croissante' : 'décroissante'} sur ${p.text}`
                : `sur ${p.text} : à découvrir`,
            )
            .join(' ; ')}
        </p>
      </section>

      {allSeen && <Note live={false}>{fn.order}</Note>}
    </div>
  );
}

/** Tableau de variations dessiné : colonnes x, flèches révélées morceau par morceau. */
function VariationTable({ fn, seen }: { fn: RefFn; seen: number[] }) {
  const W = 300;
  const H = 100;
  const X0 = 64;
  const X1 = W - 26;
  const n = fn.table.length;
  const colX = (i: number) => X0 + (i * (X1 - X0)) / (n - 1);
  const TOP = 44;
  const BOT = 90;
  return (
    <div className="mx-auto w-full max-w-[22rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-200 block w-full rounded-xl border', FIGURE_BG_CLASS)}
        aria-hidden
        data-testid="rf-table"
      >
        <line x1={0} x2={W} y1={28} y2={28} stroke="var(--color-ink-300)" />
        <line x1={42} x2={42} y1={0} y2={H} stroke="var(--color-ink-300)" />
        <text
          x={21}
          y={19}
          textAnchor="middle"
          fontSize={13}
          fontStyle="italic"
          fill="var(--color-ink-700)"
        >
          x
        </text>
        <text
          x={21}
          y={68}
          textAnchor="middle"
          fontSize={13}
          fontStyle="italic"
          fill="var(--color-ink-700)"
        >
          f(x)
        </text>
        {fn.table.map((c, i) => (
          <g key={i}>
            <text x={colX(i)} y={19} textAnchor="middle" fontSize={13} fill="var(--color-ink-900)">
              {c.x}
            </text>
            {c.bar && (
              <g stroke="var(--color-ink-700)" strokeWidth={1.5}>
                <line x1={colX(i) - 2} x2={colX(i) - 2} y1={28} y2={H} />
                <line x1={colX(i) + 2} x2={colX(i) + 2} y1={28} y2={H} />
              </g>
            )}
            {c.fx !== null && (seen.includes(i - 1) || seen.includes(i)) && (
              <text
                x={colX(i)}
                y={BOT + 4}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill="var(--color-ink-900)"
              >
                {c.fx}
              </text>
            )}
          </g>
        ))}
        {fn.pieces.map((p, i) => {
          const a = colX(i) + 14;
          const b = colX(i + 1) - 14;
          const mid = (a + b) / 2;
          if (!seen.includes(i))
            return (
              <text
                key={i}
                x={mid}
                y={(TOP + BOT) / 2 + 5}
                textAnchor="middle"
                fontSize={16}
                fontWeight={700}
                fill="var(--color-ink-400)"
              >
                ?
              </text>
            );
          const [y1, y2] = p.dir === 'up' ? [BOT - 6, TOP] : [TOP, BOT - 6];
          const ang = Math.atan2(y2 - y1, b - a);
          const head = (s: number) => `${b - 9 * Math.cos(ang + s)},${y2 - 9 * Math.sin(ang + s)}`;
          return (
            <g
              key={i}
              stroke="var(--color-primary-500)"
              strokeWidth={2.5}
              fill="none"
              data-testid={`rf-arrow-${i}`}
            >
              <line x1={a} y1={y1} x2={b} y2={y2} />
              <polyline points={`${head(0.45)} ${b},${y2} ${head(-0.45)}`} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
